<script lang="ts">
	import { Modal, Container, Text, Button, Alert } from '@Shelly/shelly-design-system';
	import { _ } from 'svelte-i18n';
	import { i18nKeyForError, type ErrorCode } from '../lib/errors';

	interface Props {
		show: boolean;
		providerName: string;
		homeName?: string;
		loading: boolean;
		error?: ErrorCode | null;
		privacyPolicyUrl?: string;
		manageLinksUrl?: string;
		scope?: string;
		onaccept: () => void;
		ondeny: () => void;
		onswitch?: () => void;
	}

	let {
		show = $bindable(false),
		providerName,
		homeName,
		loading,
		error = null,
		privacyPolicyUrl,
		manageLinksUrl,
		scope = '',
		onaccept,
		ondeny,
		onswitch,
	}: Props = $props();

	const message = $derived(
		homeName
			? $_('consent_message_with_home', {
					values: { provider: providerName, home: homeName },
				})
			: $_('consent_message', { values: { provider: providerName } }),
	);

	const errorMessage = $derived(error ? $_(i18nKeyForError(error)) : '');

	function handleClose() {
		// Backdrop / X click is treated as "Deny" to keep the flow OAuth-spec
		// correct (the modal must resolve one way or the other before we can
		// proceed or abandon the handshake).
		if (loading) return;
		ondeny();
	}

	function handleAccept() {
		if (loading) return;
		onaccept();
	}

	function handleDeny() {
		if (loading) return;
		ondeny();
	}

	function handleSwitch() {
		if (loading || !onswitch) return;
		onswitch();
	}
</script>

<Modal
	bind:showModal={show}
	title={$_('consent_title')}
	on:close={handleClose}
	backdropClose={!loading}
>
	<Container slot="content" padding="0">
		<!-- Brand attribution: Google's design checklist for OAuth consent
		     requires showing which integration is being linked. We surface
		     the Shelly logo + provider name as the integration identifier. -->
		<div class="brand-header" aria-label={$_('consent_brand_aria', { values: { provider: providerName } })}>
			<img
				src="./images/shelly-logo.svg"
				alt="Shelly"
				class="brand-logo"
			/>
			<i class="fa-solid fa-arrow-right brand-arrow" aria-hidden="true"></i>
			<span class="brand-provider">{providerName}</span>
		</div>

		{#if error}
			<Alert type="error" class="mb-m">{errorMessage}</Alert>
		{/if}
		<Text element="p" class="consent-body">{message}</Text>

		{#if scope.trim()}
			<Text element="p" class="consent-permissions">
				{$_('consent_permissions', { values: { scope: scope.trim() } })}
			</Text>
		{/if}

		{#if privacyPolicyUrl || manageLinksUrl}
			<div class="consent-links" aria-label="Account linking references">
				{#if privacyPolicyUrl}
					<a
						href={privacyPolicyUrl}
						target="_blank"
						rel="noopener noreferrer"
						class="consent-link"
					>
						{$_('consent_privacy_policy_link', {
							values: { provider: providerName },
						})}
					</a>
				{/if}
				{#if manageLinksUrl}
					<a
						href={manageLinksUrl}
						target="_blank"
						rel="noopener noreferrer"
						class="consent-link"
					>
						{$_('consent_manage_links')}
					</a>
				{/if}
			</div>
		{/if}
	</Container>

	<div slot="footer" class="consent-footer">
		{#if onswitch}
			<button
				type="button"
				class="consent-switch"
				disabled={loading}
				onclick={handleSwitch}
			>
				{$_('consent_switch_account')}
			</button>
		{/if}
		<div class="consent-footer-actions">
			<Button variant="secondary" disabled={loading} on:click={handleDeny}>
				{$_('consent_deny')}
			</Button>
			<Button variant="primary" {loading} disabled={loading} on:click={handleAccept}>
				{$_('consent_accept_link', { values: { provider: providerName } })}
			</Button>
		</div>
	</div>
</Modal>

<style>
	/*
	 * Color strategy: secondary text inherits the Modal's body color and
	 * uses opacity for visual hierarchy. The design-system's --color-2 /
	 * --color-3 tokens are tuned for light backgrounds and wash out on the
	 * dark Modal; `inherit` + opacity stays legible in both themes.
	 */

	:global(.consent-body) {
		padding: var(--spacing-m, 16px) 0;
		line-height: 1.5;
	}
	.consent-footer {
		display: flex;
		justify-content: space-between;
		align-items: center;
		gap: 12px;
		width: 100%;
		flex-wrap: wrap;
	}
	.consent-footer-actions {
		display: flex;
		gap: 12px;
		margin-left: auto;
	}
	/* Tertiary text button for "Use a different account". Plain <button>
	   instead of design-system Button to keep visual weight low — Google's
	   guideline says switch-account should be discoverable but not compete
	   with Accept/Deny. */
	.consent-switch {
		background: none;
		border: none;
		padding: 4px 0;
		color: inherit;
		opacity: 0.75;
		font: inherit;
		font-size: 14px;
		text-decoration: underline;
		cursor: pointer;
	}
	.consent-switch:hover:not(:disabled) {
		opacity: 1;
	}
	.consent-switch:disabled {
		opacity: 0.4;
		cursor: not-allowed;
	}
	/* Brand attribution row: Shelly logo → provider name. Matches Google's
	   account-linking consent design guideline (integration identification). */
	.brand-header {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 14px;
		padding: 4px 0 var(--spacing-s, 12px);
		border-bottom: 1px solid color-mix(in srgb, currentColor 18%, transparent);
		margin-bottom: var(--spacing-m, 16px);
	}
	.brand-logo {
		height: 28px;
		width: auto;
	}
	.brand-arrow {
		color: inherit;
		opacity: 0.55;
		font-size: 14px;
	}
	.brand-provider {
		font-weight: 600;
		font-size: 18px;
		color: inherit;
	}
	/* Privacy policy + manage-links row. Small, secondary, beneath the
	   message so it doesn't compete with the Accept/Deny CTA. */
	.consent-links {
		display: flex;
		flex-wrap: wrap;
		gap: 16px;
		font-size: 13px;
		padding-top: 4px;
	}
	.consent-link {
		color: inherit;
		opacity: 0.8;
		text-decoration: underline;
	}
	.consent-link:hover {
		opacity: 1;
	}
	/* "Permissions: openid email profile" — surfaces requested OAuth scopes
	   so the user knows what they're granting. Hidden when scope is empty
	   (Smart Home today). */
	:global(.consent-permissions) {
		margin-top: -4px;
		padding-bottom: var(--spacing-s, 8px);
		font-size: 13px;
		opacity: 0.8;
		word-break: break-word;
	}
</style>
